import { GlobalStore } from '@highlight/ipc'
import { isAlphaOrDynamicChannel, isProductionChannel } from './builds/channels.shared'

export const PRODUCTION_CHAT_BACKEND = 'https://chat-backend.highlightai.com/'
export const STAGING_CHAT_BACKEND = 'https://chat-backend-staging.highlightai.com/'
export const LOCAL_CHAT_BACKEND = 'http://localhost:8787/'

/**
 * The shared, long-lived backends — i.e. every backend that is *not* a specific
 * PR's ephemeral one. Used to tell "this build was pointed at its own ephemeral
 * backend" apart from "this build fell through to the staging default".
 */
export const STATIC_CHAT_BACKENDS: ReadonlyArray<string> = [
  PRODUCTION_CHAT_BACKEND,
  STAGING_CHAT_BACKEND,
  LOCAL_CHAT_BACKEND,
]

/**
 * Explicit user choice from Settings > Backend Host. Highest priority when
 * set. Read via getChatApiUrl() below, which only ever honors it on
 * alpha/dynamic builds (never production/beta/latest/trusted-testers) — so a
 * value stuck in a synced store can't silently redirect a real user's app.
 */
export function getBackendHostOverride(): string {
  return GlobalStore.settings().get<string>('backendHostOverride') ?? ''
}

export function setBackendHostOverride(url: string | null): void {
  GlobalStore.settings().set('backendHostOverride', url ?? '')
}

/**
 * Trailing slash is load-bearing: not every consumer builds its URL with
 * `new URL(path, base)`. The ASR websocket string-concatenates onto this
 * (`${host}api/v1/transcribe-ws`), so a base without the slash silently yields
 * `…workers.devapi/v1/…` and misses the backend entirely. Every entry in
 * STATIC_CHAT_BACKENDS carries one; normalize anything from a build var, a
 * persisted value, or a user-typed Custom URL to match.
 */
function withTrailingSlash(url: string): string {
  return url.endsWith('/') ? url : `${url}/`
}

/**
 * This build's own ephemeral backend, baked in at build time by build.yml from
 * the deploy's output. Empty when this build has none — including when it fell
 * through to `.env.staging`, hence the static-host filter.
 */
export function bakedInEphemeralBackendUrl(): string {
  const url = import.meta.env.VITE_HIGHLIGHT_CHAT_BACKEND_URL
  if (typeof url !== 'string' || url.length === 0) {
    return ''
  }
  const normalized = withTrailingSlash(url)
  return STATIC_CHAT_BACKENDS.includes(normalized) ? '' : normalized
}

/**
 * Backend URL auto-detected as this dynamic build's own PR backend (set once
 * a reachability check confirms it — see useAutoDetectEphemeralBackend).
 * Lower priority than an explicit override, higher than the staging default.
 *
 * Only ever honors a value matching *this* build's baked-in backend. The store
 * lives in a userData folder shared by every install on the machine, so without
 * that check a value left by an earlier PR build would be trusted here
 * synchronously — before the async health check that would clear it has run,
 * and for builds (alpha, or a PR with no ephemeral backend) whose detection
 * hook early-returns and never clears it at all. Tying it to the baked-in URL
 * makes "use someone else's backend" unrepresentable rather than merely
 * unlikely.
 */
export function getAutoDetectedBackendHost(): string {
  const stored = GlobalStore.settings().get<string>('backendHostAutoDetected') ?? ''
  if (!stored) {
    return ''
  }
  const normalized = withTrailingSlash(stored)
  return normalized === bakedInEphemeralBackendUrl() ? normalized : ''
}

export function setAutoDetectedBackendHost(url: string | null): void {
  GlobalStore.settings().set('backendHostAutoDetected', url ?? '')
}

/** The cloudflared login command to run when the Backend Host sits behind Cloudflare Access with no token on disk, else ''. */
export function getBackendHostAccessError(): string {
  return GlobalStore.settings().get<string>('backendHostAccessError') ?? ''
}

export function setBackendHostAccessError(message: string): void {
  GlobalStore.settings().set('backendHostAccessError', message)
}

export const getChatApiUrl = () => {
  const isDevEnvSet = process.env.NODE_ENV === 'development'
  const isLocalEnvSet =
    isDevEnvSet &&
    (process.env.USE_LOCAL_HL_CHAT_BACKEND === 'true' || import.meta.env.VITE_USE_LOCAL_HL_CHAT_BACKEND === 'true')
  const isProdEnvSet = isDevEnvSet && process.env.USE_PROD_CHAT_BACKEND === 'true'

  if (isLocalEnvSet) {
    return LOCAL_CHAT_BACKEND
  }

  // Manual override (Settings > Backend Host) or an auto-detected match for
  // this build's own PR backend. Gated the same way the Backend Host control
  // itself is gated (alpha or dynamic build) so it's a no-op everywhere else.
  if (isDevEnvSet || isAlphaOrDynamicChannel()) {
    const chosenBackend = getBackendHostOverride() || getAutoDetectedBackendHost()
    if (chosenBackend) {
      // Normalized here too: an override can be a URL the user typed into
      // "Custom…", which won't reliably carry the trailing slash concatenating
      // call sites depend on.
      return withTrailingSlash(chosenBackend)
    }
  }

  const shouldUseProduction = (isDevEnvSet && isProdEnvSet) || (!isDevEnvSet && isProductionChannel())
  const shouldUseStaging =
    (isDevEnvSet && !isLocalEnvSet && !isProdEnvSet) || (!shouldUseProduction && isAlphaOrDynamicChannel())

  if (shouldUseStaging) {
    return STAGING_CHAT_BACKEND
  }
  return PRODUCTION_CHAT_BACKEND
}

export const getChatApiRoute = (route: string) => {
  return new URL(route, getChatApiUrl()).toString()
}

export const getBackendUrl = () => {
  const DEV_MODE = process.env.USE_LOCAL_HL_CHAT_BACKEND === 'true'
  return DEV_MODE ? 'http://localhost:7777' : 'https://backend.highlightai.com'
}
